import type { FC, ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { PapiStudioSellerStatsInit } from '@wix/da-papi-types';
import { useMeasureElement } from '@wix/da-hooks/pkg/useMeasureElement/useMeasureElement';
import { getCoreLevel } from '@wix/da-shared-react/pkg/publicSession/selectors';
import UserAvatar from '@wix/da-shared-react/pkg/User/Avatar';
import UserLink from '@wix/da-shared-react/pkg/User/Link';
import TopCustomerIcon, {
  Props as TopCustomerIconProps,
} from '@wix/da-ds/pkg/Icons/32x32/TopCustomer';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import { formatDate, DATE_FORMATS } from '@wix/da-i18next/pkg/time/functions';
import { formatDollars } from '@wix/da-shared-react/pkg/utils/formatting';
import ReportsSection from '../ReportsSection';
import ReportsTable from '../ReportsTable';
import CoreUpsellRow from '../ReportsTable/CoreUpsellRow';
import TopCustomersActionMenu from './TopCustomersActionMenu';

import s from './TopCustomersSection.scss';

export interface Props {
  className?: string;
  data?: PapiStudioSellerStatsInit['topCustomers'];
}

const EXPANDED_ACTIONS_TABLE_WIDTH = 1160;

const TopCustomersSection: FC<Props> = ({ data, className }) => {
  const { t, i18n } = useTranslation();
  const { ref: tableRef, dimensions: tableDimensions } = useMeasureElement({
    throttle: 250,
    throttleOptions: { trailing: true },
  });
  const [expandedActions, setExpandedActions] = useState<boolean>(false);

  useEffect(() => {
    setExpandedActions(
      typeof tableDimensions.width === 'number' &&
        Math.floor(tableDimensions.width) >= EXPANDED_ACTIONS_TABLE_WIDTH
    );
  }, [tableDimensions.width]);

  const userCoreLevel = useSelector(getCoreLevel);
  const hasCoreProPlus =
    userCoreLevel === 'core_pro_plus' || userCoreLevel === 'core_max';
  const columns = useMemo(() => {
    return [
      {
        key: 'customer',
        label: t('studio.reports.section.topCustomers.table.header.customer'),
      },
      {
        key: 'last_purchase_date',
        label: t(
          'studio.reports.section.topCustomers.table.header.lastPurchase'
        ),
      },
      {
        key: 'unique_products',
        label: t(
          'studio.reports.section.topCustomers.table.header.uniqueProducts'
        ),
      },
      {
        key: 'purchases',
        label: t('studio.reports.section.topCustomers.table.header.purchases'),
      },
      {
        key: 'revenue',
        label: t('studio.reports.section.topCustomers.table.header.revenue'),
      },
      {
        key: 'actions',
        label: t('studio.reports.section.topCustomers.table.header.actions'),
      },
    ];
  }, [t]);

  if (!data) {
    return null;
  }

  return (
    <ReportsSection
      reportType="topCustomers"
      className={className}
      title={t('studio.reports.section.topCustomers.title')}
      description={t('studio.reports.section.topCustomers.description', {
        postProcess: ['reactPostprocessor'],
        learnMoreLink: (
          <a
            className="text-link-level-2-tertiary"
            href={t(
              'studio.reports.section.topCustomers.description.learnMoreLink.url'
            )}
          >
            {t(
              'studio.reports.section.topCustomers.description.learnMoreLink.text'
            )}
          </a>
        ),
      })}
      data={data}
      dataFilter={dataToFilter =>
        /* show top 50 customers for core members */
        dataToFilter?.slice(0, hasCoreProPlus ? 50 : 3)
      }
      renderTable={(
        filteredData: any[],
        _,
        unfilteredData: any[]
      ) => (
        <ReportsTable
          tableRef={tableRef}
          columns={columns}
          data={filteredData}
          initialVisibleCount={5}
          renderRow={(
            {
              user,
              lastPurchaseDate,
              uniqueProducts,
              totalSales,
              salesVolume,
            }: any,
            index: number
          ) => {
            let overlayIcon: ReactNode | null = null;
            // The first three rows have an icon overlaid on the avatar
            if (index >= 0 && index < 3) {
              overlayIcon = (
                <TopCustomerIcon
                  className={s['overlay-icon']}
                  position={(index + 1) as TopCustomerIconProps['position']}
                  size={IconSize.LARGE}
                />
              );
            }
            return (
              <>
                <td>
                  <div className={s['customer']}>
                    <UserAvatar
                      className={s['avatar']}
                      user={user}
                      size={40}
                      overlayIcon={overlayIcon}
                    />
                    <UserLink user={user} className={s['userlink']} />
                  </div>
                </td>
                <td>
                  {formatDate(
                    lastPurchaseDate,
                    DATE_FORMATS.Date.Full,
                    i18n.language
                  )}
                </td>
                <td>{uniqueProducts}</td>
                <td>{totalSales}</td>
                <td>{formatDollars(salesVolume)}</td>
                <td>
                  <TopCustomersActionMenu
                    user={user}
                    wrapperClassName={s['menu-wrapper']}
                    expandedActions={expandedActions}
                  />
                </td>
              </>
            );
          }}
          renderFooter={() => (
            unfilteredData.length > 3 && !hasCoreProPlus && <CoreUpsellRow />
          )}
        />
      )}
    />
  );
};
TopCustomersSection.displayName = 'TopCustomersSection';

export default TopCustomersSection;
